<template>
  <Container hideFooter title="Pickup or delivery">
    <template v-slot:body>
      <div class="flow-layout logistics-flow">
        <section class="flow-intro">
          <div class="step-badge step-badge--accent">
            <span>01</span><i></i><span>LOGISTICS</span>
          </div>
          <h1>Let’s move<br /><span>things along.</span></h1>
          <p class="flow-lede">
            Share a few details about your stop so our team can get you to the
            right place quickly.
          </p>

          <div class="logistics-art" aria-hidden="true">
            <div class="logistics-box logistics-box--one">
              <span></span><span></span>
            </div>
            <div class="logistics-box logistics-box--two">
              <span></span><span></span>
            </div>
            <div class="logistics-path"></div>
            <span class="logistics-art-label">DOCK<br />ACCESS</span>
          </div>

          <div class="trust-row logistics-trust">
            <span class="trust-icon"><IconGlyph name="truck" :size="20"/></span>
            <span
              ><strong>We’ll take it from here</strong
              ><small>Thanks for helping us keep things moving.</small></span
            >
          </div>
        </section>

        <section
          class="form-card logistics-card"
          aria-label="Pickup or delivery form"
        >
          <div class="form-card-heading">
            <div>
              <span class="section-overline">A few quick details</span>
              <h2>Tell us about your stop</h2>
            </div>
            <span class="required-note"><span>*</span> Required</span>
          </div>

          <form @submit.prevent="handleSignIn">
            <div class="field-group">
              <label class="field-label" for="carrier"
                >Carrier or company <span>*</span></label
              >
              <div class="select-wrap">
                <select id="carrier" v-model="carrier" class="kiosk-select">
                  <option disabled value="">Select a carrier</option>
                  <option v-for="item in carriers" :key="item" :value="item">{{
                    item
                  }}</option>
                </select>
                <IconGlyph name="chevron-down" :size="18" />
              </div>
            </div>

            <div v-if="carrier === 'Other'" class="field-group">
              <label class="field-label" for="other-carrier"
                >Carrier or company name <span>*</span></label
              >
              <input
                id="other-carrier"
                v-model="otherCarrier"
                class="kiosk-input"
                autocomplete="off"
                placeholder="Enter company name"
                type="text"
              />
            </div>

            <div class="field-group">
              <span class="field-label">This is a <span>*</span></span>
              <div class="delivery-choice-grid">
                <button
                  v-for="item in items"
                  :key="item.value"
                  class="delivery-choice"
                  :class="{
                    'delivery-choice--selected': pickup === item.value
                  }"
                  type="button"
                  @click="pickup = item.value"
                >
                  <span class="delivery-choice-icon"
                    ><IconGlyph
                      :name="item.value === 'pickup' ? 'package' : 'truck'"
                      :size="22"
                  /></span>
                  <span
                    ><strong>{{ item.text }}</strong
                    ><small>{{
                      item.value === "pickup"
                        ? "I’m collecting items"
                        : "I’m dropping items off"
                    }}</small></span
                  >
                  <span class="delivery-choice-check"
                    ><IconGlyph name="check" :size="13"
                  /></span>
                </button>
              </div>
            </div>

            <div class="field-group packages-group">
              <label class="field-label" for="packages"
                >Number of packages or pallets
                <span class="optional">Optional</span></label
              >
              <div class="packages-input-wrap">
                <input
                  id="packages"
                  v-model="packages"
                  class="kiosk-input"
                  autocomplete="off"
                  inputmode="numeric"
                  placeholder="e.g. 4"
                  type="tel"
                />
                <button
                  type="button"
                  class="unknown-button"
                  @click="packages = 'Unknown'"
                >
                  Not sure?
                </button>
              </div>
            </div>

            <div class="form-card-bottom">
              <span class="form-hint"
                ><span class="hint-dot"></span> Someone from our team will be
                with you shortly.</span
              >
              <button
                class="primary-cta"
                name="submit"
                type="submit"
                :disabled="submitting"
              >
                <span>{{ submitting ? "Sending…" : "Continue" }}</span>
                <IconGlyph v-if="!submitting" name="arrow-right" :size="20" />
              </button>
            </div>
          </form>
        </section>
      </div>
    </template>
  </Container>
</template>

<script>
import Container from "@/components/Container";
import IconGlyph from "@/components/IconGlyph";

export default {
  name: "Pickup",
  components: { Container, IconGlyph },
  data() {
    return {
      otherCarrier: "",
      pickup: "",
      carrier: "",
      packages: null,
      submitting: false,
      items: [
        { text: "Pickup", value: "pickup" },
        { text: "Delivery", value: "delivery" }
      ],
      carriers: ["Amazon", "UPS", "FedEx", "USPS", "DHL", "Other"]
    };
  },
  methods: {
    async handleSignIn() {
      if (
        !this.carrier ||
        (this.carrier === "Other" && !this.otherCarrier.trim())
      ) {
        this.$store.dispatch("setSnackbar", {
          text: "Please choose or enter your carrier.",
          color: "error"
        });
        return;
      }
      if (!this.pickup) {
        this.$store.dispatch("setSnackbar", {
          text: "Please choose pickup or delivery.",
          color: "error"
        });
        return;
      }

      this.submitting = true;
      try {
        await this.$store.dispatch("signIn", {
          reason: this.pickup,
          company:
            this.carrier === "Other" ? this.otherCarrier.trim() : this.carrier,
          packages: this.packages ? this.packages : "Unknown",
          type: this.pickup
        });
        this.reset();
      } finally {
        this.submitting = false;
      }
    },
    reset() {
      this.otherCarrier = "";
      this.pickup = "";
      this.carrier = "";
      this.packages = null;
    }
  }
};
</script>

<style>
.step-badge--accent {
  color: var(--accent);
}

.step-badge--accent i {
  background: var(--accent);
}

.logistics-flow .flow-intro h1 span {
  color: var(--orange);
}

.logistics-art {
  position: relative;
  width: 100%;
  height: 152px;
  margin: 33px 0 28px;
  overflow: hidden;
  border-radius: 21px;
  background: #eeeee8;
}

.logistics-art::before,
.logistics-art::after {
  position: absolute;
  border: 1px solid rgba(77, 86, 84, 0.18);
  border-radius: 50%;
  content: "";
}

.logistics-art::before {
  top: -70px;
  right: -35px;
  width: 204px;
  height: 204px;
}

.logistics-art::after {
  top: -47px;
  right: -12px;
  width: 158px;
  height: 158px;
}

.logistics-box {
  position: absolute;
  z-index: 1;
  width: 74px;
  height: 67px;
  border-radius: 9px;
  background: #df9b53;
  box-shadow: 8px 10px 0 rgba(77, 86, 84, 0.08);
  transform: rotate(-8deg);
}

.logistics-box--one {
  bottom: 24px;
  left: 69px;
}

.logistics-box--two {
  bottom: 35px;
  left: 132px;
  width: 61px;
  height: 57px;
  background: #f3bd6d;
  transform: rotate(9deg);
}

.logistics-box span {
  position: absolute;
  top: 18px;
  left: 12px;
  width: 49px;
  height: 2px;
  background: rgba(112, 68, 28, 0.25);
  transform: rotate(-19deg);
}

.logistics-box span + span {
  top: 30px;
  width: 32px;
}

.logistics-path {
  position: absolute;
  bottom: 26px;
  left: 38px;
  width: 170px;
  height: 1px;
  background: rgba(77, 86, 84, 0.28);
  transform: rotate(-1deg);
}

.logistics-art-label {
  position: absolute;
  top: 35px;
  left: 254px;
  color: var(--accent);
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.17em;
  line-height: 1.45;
}

.logistics-trust {
  margin-top: 4px;
}

.logistics-card {
  max-width: 680px;
}

.select-wrap {
  position: relative;
}

.select-wrap .kiosk-select {
  appearance: none;
  padding-right: 45px;
  cursor: pointer;
}

.select-wrap > .icon-glyph {
  position: absolute;
  top: 17px;
  right: 15px;
  pointer-events: none;
  color: var(--muted);
}

.delivery-choice-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.delivery-choice {
  display: flex;
  align-items: center;
  min-height: 76px;
  padding: 12px 13px;
  border: 1px solid #dfe3dd;
  border-radius: 12px;
  background: #fbfbf9;
  color: var(--ink);
  text-align: left;
  cursor: pointer;
  transition: 160ms ease;
}

.delivery-choice:hover {
  border-color: #d7b58f;
  background: #f8f4ec;
}

.delivery-choice--selected {
  border-color: var(--accent);
  background: #f5eee6;
  box-shadow: inset 0 0 0 1px var(--accent);
}

.delivery-choice-icon {
  display: grid;
  width: 38px;
  height: 38px;
  margin-right: 10px;
  border-radius: 11px;
  background: #f9e5d5;
  color: var(--orange);
  place-items: center;
}

.delivery-choice:nth-child(2) .delivery-choice-icon {
  background: var(--accent-soft);
  color: var(--accent);
}

.delivery-choice > span:nth-child(2) {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 5px;
}

.delivery-choice strong {
  font-size: 12px;
}

.delivery-choice small {
  color: var(--muted);
  font-size: 9px;
}

.delivery-choice-check {
  display: grid;
  width: 18px;
  height: 18px;
  margin-left: 8px;
  border: 1px solid #c9d0cc;
  border-radius: 50%;
  color: transparent;
  place-items: center;
}

.delivery-choice--selected .delivery-choice-check {
  border-color: var(--accent);
  background: var(--accent);
  color: white;
}

.packages-input-wrap {
  position: relative;
}

.packages-input-wrap .kiosk-input {
  padding-right: 95px;
}

.unknown-button {
  position: absolute;
  top: 1px;
  right: 2px;
  height: 50px;
  padding: 0 13px;
  border: 0;
  border-left: 1px solid #e4e8e2;
  border-radius: 0 11px 11px 0;
  background: transparent;
  color: var(--accent);
  font: inherit;
  font-size: 10px;
  font-weight: 850;
  cursor: pointer;
}

.unknown-button:hover {
  background: var(--accent-soft);
}

@media (max-width: 1000px) {
  .logistics-art {
    max-width: 420px;
  }
}

@media (max-width: 620px) {
  .delivery-choice-grid {
    grid-template-columns: 1fr;
  }
}
</style>
